import { useEffect, useState } from "react";
import { ICON_BASE_PATH } from "@/app/utils/config";
import { gxfileviewApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import NoDataFound from "@/app/components/common/warning/NoDataFound";

// === Types ===============================================================

interface CompanyDocument {
  fileName: string;
  filePath?: string;
  base64Output: string;
  type: string;
  fileExtensionType?: string;
}

interface CompanyDocumentApiResponse {
  status: number;
  data?: CompanyDocument[];
  errorMessage?: string;
}

interface DisplayDocument extends CompanyDocument {
  docType: string;
}

interface ProfileStatementsProps {
  docTypes?: string[];
  vcifId?: string | null;
}

const DEFAULT_DOC_TYPES = ["LOGO"];
const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "svg", "gif", "webp"];

// === Helpers ===============================================================

// ............................... toTitleCase ................................
const toTitleCase = (value: string): string =>
  value
    .toLowerCase()
    .split(" ")
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(" ");

// ............................... getFileExtension ................................
const getFileExtension = (fileName: string): string =>
  fileName.split(".").pop()?.toLowerCase() || "";

// ............................... isImageFile ................................
const isImageFile = (fileName: string): boolean =>
  IMAGE_EXTENSIONS.includes(getFileExtension(fileName));

// ............................... getMimeType ................................
const getMimeType = (extension: string): string => {
  switch (extension) {
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "svg":
      return "image/svg+xml";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "pdf":
      return "application/pdf";
    case "txt":
      return "text/plain";
    default:
      return "application/octet-stream";
  }
};

// ............................... base64ToBlobUrl ................................
const base64ToBlobUrl = (base64Output: string, fileName: string): string => {
  const byteCharacters = atob(base64Output);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const mimeType = getMimeType(getFileExtension(fileName));
  const blob = new Blob([byteArray], { type: mimeType });

  return window.URL.createObjectURL(blob);
};

// ............................... fetchDocumentType (deduped) ................................
const inFlightDocumentRequests = new Map<string, Promise<DisplayDocument[]>>();

const fetchDocumentType = (
  vcifId: string,
  documentType: string,
): Promise<DisplayDocument[]> => {
  const cacheKey = `${vcifId}::${documentType}`;
  const inFlight = inFlightDocumentRequests.get(cacheKey);
  if (inFlight) return inFlight;

  const request = gxfileviewApi({ documentType, vcifId })
    .then((response: CompanyDocumentApiResponse) => {
      if (response.status === 1 && response.data) {
        return response.data.map((doc) => ({ ...doc, docType: documentType }));
      }
      return [] as DisplayDocument[];
    })
    .finally(() => {
      inFlightDocumentRequests.delete(cacheKey);
    });

  inFlightDocumentRequests.set(cacheKey, request);
  return request;
};

// === Component ============================================================

const ProfileStatements = ({ docTypes, vcifId }: ProfileStatementsProps) => {
  const [documents, setDocuments] = useState<DisplayDocument[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const requestedTypes =
    docTypes && docTypes.length > 0 ? docTypes : DEFAULT_DOC_TYPES;
  const uniqueTypesKey = Array.from(new Set(requestedTypes)).join("|");

  // ............................... fetchDocuments ................................
  useEffect(() => {
    const types = uniqueTypesKey.split("|").filter(Boolean);
    let cancelled = false;

    const fetchDocuments = async () => {
      if (!vcifId || types.length === 0) {
        setDocuments([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const results = await Promise.all(
          types.map((documentType) => fetchDocumentType(vcifId, documentType)),
        );

        if (!cancelled) setDocuments(results.flat());
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching company documents:", err);
          setError("Error fetching documents");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchDocuments();

    return () => {
      cancelled = true;
    };
  }, [vcifId, uniqueTypesKey]);

  // ............................... viewDocument ................................
  const viewDocument = (doc: DisplayDocument) => {
    if (doc.base64Output) {
      const url = base64ToBlobUrl(doc.base64Output, doc.fileName);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } else if (doc.filePath) {
      window.open(
        `${doc.filePath}\\${doc.fileName}`,
        "_blank",
        "noopener,noreferrer",
      );
    }
  };

  // ............................... downloadDocument ................................
  const downloadDocument = (doc: DisplayDocument, key: string) => {
    if (!doc.base64Output) return;
    setDownloading(key);
    try {
      const url = base64ToBlobUrl(doc.base64Output, doc.fileName);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = doc.fileName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading document:", err);
    } finally {
      setDownloading(null);
    }
  };

  // ............................... Error State ................................
  if (error) {
    return (
      <div className="gx-stmt-container">
        <div className="gx-stmt-box">
          <div className="flex justify-center items-center py-8">
            <div className="text-[0.875rem] text-[#ff0000]">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  // ............................... Render ................................
  return (
    <div className="gx-stmt-container">
      <div className="gx-stmt-box gx-profile-statement-box">
        {documents.length === 0 ? (
          <div>
            <NoDataFound />
          </div>
        ) : (
          <div className="gx-profile-statement-icons">
            {documents.map((doc, index) => {
              const key = `${doc.docType}-${doc.fileName}-${index}`;
              const showThumbnail =
                doc.base64Output && isImageFile(doc.fileName);

              return (
                <div key={key} className="gx-profile-statement-card">
                  <div className="gx-profile-statement-details">
                    <div className="gx-stmt-card-left">
                      {showThumbnail ? (
                        <img
                          src={`data:${getMimeType(
                            getFileExtension(doc.fileName),
                          )};base64,${doc.base64Output}`}
                          alt={doc.docType}
                          className="gx-stmt-pdf-icon gx-stmt-thumb"
                        />
                      ) : (
                        <img
                          src={`${ICON_BASE_PATH}pdf-icon.svg`}
                          alt="file icon"
                          className="gx-stmt-pdf-icon"
                        />
                      )}
                      <div className="gx-profile-file-details">
                        <span className="gx-p12-m">
                          {toTitleCase(doc.docType)}
                        </span>
                        <div className="gx-profile-statement-date gx-p10-m">
                          {doc.fileName}
                        </div>
                      </div>
                    </div>

                    <div className="gx-stmt-card-right flex items-center gap-3">
                      <button
                        onClick={() => viewDocument(doc)}
                        disabled={!doc.base64Output && !doc.filePath}
                        className="gx-stmt-view"
                        title={`View ${doc.fileName}`}
                      >
                        <img
                          src={`${ICON_BASE_PATH}eyeicon.svg`}
                          alt="view"
                          className={`gx-stmt-view-icon ${
                            !doc.base64Output && !doc.filePath
                              ? "gx-stmt-view-disabled"
                              : ""
                          }`}
                        />
                      </button>

                      <button
                        onClick={() => downloadDocument(doc, key)}
                        disabled={!doc.base64Output || downloading === key}
                        className="gx-stmt-download"
                        title={`Download ${doc.fileName}`}
                      >
                        {downloading === key ? (
                          <div className="gx-stmt-download-loading">
                            <span>...</span>
                          </div>
                        ) : (
                          <img
                            src={`${ICON_BASE_PATH}downloadicon.svg`}
                            alt="download"
                            className={`gx-profile-statement-download-icon ${
                              !doc.base64Output
                                ? "gx-stmt-download-disabled"
                                : ""
                            }`}
                          />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileStatements;
